import { observable, action } from "mobx";
import CreditAccountRepository from "~/repository/CreditAccountRepository";
import CreditAccount from "~/models/CreditAccount";

/**
 * This service assumes all credit account records are scoped to a single provider at a time.
 * If mixed credit accounts are required then this class should be tweaked, mainly on how balance is used.
 */
class CreditAccountStore {
  @observable isLoading = false;

  @observable lastQuery = {};

  @observable creditAccounts = [];

  @observable total = 0;

  @observable balance = 0;

  creditAccountRepository = null;

  constructor() {
    this.creditAccountRepository = new CreditAccountRepository();
  }

  @action
  load(query = {}, blockingLoad = true) {
    this.lastQuery = query;
    this.isLoading = blockingLoad;

    return this.creditAccountRepository.fetch(query).then(response => {
      const { creditAccounts, balance, total } = response.data;

      this.total = total;
      this.balance = balance;
      this.receiveCreditAccounts(creditAccounts);
      this.isLoading = false;
    });
  }

  @action
  reload(blockingLoad = true) {
    return this.load(this.lastQuery, blockingLoad);
  }

  @action
  receiveCreditAccounts(records) {
    this.creditAccounts.clear();
    records.forEach(record => this.creditAccounts.push(new CreditAccount(record)));
  }

  @action
  update(creditAccountId, data) {
    this.isLoading = true;

    const creditAccount = this.getCreditAccountByIdOrBail(creditAccountId);

    return this.creditAccountRepository
      .update(creditAccountId, data)
      .then(response => {
        const { balance } = response.data;
        // update to reflect the current changes for perceived speed. They will be re-fetched eventually.
        creditAccount.setAmount(data.amount);
        creditAccount.setDescription(data.description);
        // negative value so it performs a subtraction
        this.balance = balance;
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  @action
  delete(creditAccountId) {
    this.isLoading = true;

    // make sure the record exists in our store.
    const creditAccount = this.getCreditAccountByIdOrBail(creditAccountId);

    return this.creditAccountRepository
      .delete(creditAccountId)
      .then(response => {
        const { balance } = response.data;

        this.balance = balance;
        this.removeCreditAccount(creditAccount);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  @action
  removeCreditAccount(creditAccount) {
    const indexAt = this.creditAccounts.indexOf(creditAccount);

    if (indexAt === -1) {
      throw new Error(`Unable to find credit account with id #${creditAccount.creditAccountId}`);
    }

    this.creditAccounts.splice(indexAt, 1);
  }

  @action
  createCreditAccount(providerId, data) {
    this.isLoading = true;

    return this.creditAccountRepository
      .create(providerId, data)
      .then(response => {
        const { creditAccount, balance } = response.data;
        // add the creditAccount to the stack but only if all credit accounts belong to the same provider
        if (
          this.creditAccounts.length === 0 ||
          this.creditAccounts.every(record => record.providerId === creditAccount.providerId)
        ) {
          this.creditAccounts.push(new CreditAccount(creditAccount));
          this.balance = balance;
        } else {
          throw new Error("Not all credit accounts belong to the same provider");
        }
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  @action
  createCreditAccounts(data) {
    this.isLoading = true;

    return this.creditAccountRepository.createMany(data).finally(() => {
      this.isLoading = false;
    });
  }

  @action
  getCreditAccountByIdOrBail(creditAccountId) {
    // find the corresponding record.
    const creditAccount = this.creditAccounts.find(record => record.creditAccountId === creditAccountId);

    if (!creditAccount) {
      throw new Error(`Could not find creditAccount record with id: ${creditAccountId}`);
    }

    return creditAccount;
  }
}

export default CreditAccountStore;
